/**
 * Vacinas exigidas da empresa (#530, spec D2 e D10).
 *
 * A empresa marca o que cobra de todos; cada unidade ativa só acrescenta. Na lista da unidade,
 * o que já é da empresa aparece marcado e travado, porque o servidor recusa repetir ali. Cada
 * clique grava o conjunto inteiro daquele nível — não há botão de salvar para esquecer.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Typography,
} from '@mui/material';
import { Vaccines as VaccinesIcon } from '@mui/icons-material';
import { PrerequisiteBlock, ProfileSection } from '../../../../components/common';
import { useAcao } from '../../../../authorization/useVeredito';
import { useDadoPermitido, useListaPermitida } from '../../../../authorization/useListaPermitida';
import { vaccineTypeService } from '../../../../services/vaccineTypeService';
import { vaccineRequirementService } from '../../../../services/vaccineRequirementService';
import { unitService } from '../../../../services/unitService';
import { getApiErrorMessage } from '../../../../types/common';
import type { Unit } from '../../../../types/unit';
import { legendaDaVacina, type ExigenciasDaEmpresa, type VaccineType } from '../../../../types/vaccination';

interface CompanyVaccinesTabProps {
  companyId: string;
}

interface OpcaoDeVacina {
  vacina: VaccineType;
  marcada: boolean;
  travada: boolean;
  legenda: string;
}

const GRADE_DE_OPCOES = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  columnGap: 2,
};

const ListaDeVacinas: React.FC<{
  titulo: string;
  descricao: string;
  opcoes: OpcaoDeVacina[];
  desabilitada: boolean;
  onAlternar: (vacina: VaccineType) => void;
}> = ({ titulo, descricao, opcoes, desabilitada, onAlternar }) => (
  <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
    <FormLabel component="legend" sx={{ fontWeight: 600, color: 'text.primary' }}>
      {titulo}
    </FormLabel>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
      {descricao}
    </Typography>
    <Box sx={GRADE_DE_OPCOES}>
      {opcoes.map(({ vacina, marcada, travada, legenda }) => (
        <FormControlLabel
          key={vacina.id}
          control={
            <Checkbox
              checked={marcada}
              disabled={desabilitada || travada}
              onChange={() => onAlternar(vacina)}
            />
          }
          label={
            <Box>
              <Typography variant="body2">{vacina.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {legenda}
              </Typography>
            </Box>
          }
        />
      ))}
    </Box>
  </FormControl>
);

const CompanyVaccinesTab: React.FC<CompanyVaccinesTabProps> = ({ companyId }) => {
  const alterar = useAcao('vaccine-requirement.update', 'Só leitura');

  const carregarCatalogo = useCallback(() => vaccineTypeService.getAll(), []);
  const carregarExigencias = useCallback(() => vaccineRequirementService.get(companyId), [companyId]);
  const carregarUnidades = useCallback(() => unitService.getUnitsByCompanyId(companyId), [companyId]);

  const catalogo = useListaPermitida<VaccineType>('vaccine-type.read', 'Catálogo de vacinas', carregarCatalogo);
  const requisitos = useDadoPermitido<ExigenciasDaEmpresa>(
    'vaccine-requirement.read',
    'Vacinas exigidas',
    carregarExigencias
  );
  const unidades = useListaPermitida<Unit>('unit.read', 'Unidades', carregarUnidades);

  // O que se desenha é o carregado, até a primeira gravação devolver o conjunto novo. Uma cópia
  // sincronizada por efeito desenhava a lista desmarcada no primeiro quadro depois da carga.
  const [gravadas, setGravadas] = useState<ExigenciasDaEmpresa | null>(null);
  const exigencias = gravadas ?? requisitos.dado;
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setGravadas(null);
  }, [requisitos.dado]);

  const daEmpresa = new Set(exigencias?.empresa ?? []);
  const daUnidade = (unitId: string): string[] =>
    exigencias?.unidades.find((unidade) => unidade.unitId === unitId)?.vaccineTypeIds ?? [];

  // Vacina inativa some do seletor, mas continua valendo para quem já a exige (spec D1):
  // escondê-la faria a exigência existir sem que a tela mostrasse.
  const exigidasEmAlgumNivel = new Set([
    ...(exigencias?.empresa ?? []),
    ...(exigencias?.unidades.flatMap((unidade) => unidade.vaccineTypeIds) ?? []),
  ]);
  const ativas = catalogo.itens.filter((vacina) => vacina.status === 'ACTIVE');
  const vacinas = catalogo.itens.filter(
    (vacina) => vacina.status === 'ACTIVE' || exigidasEmAlgumNivel.has(String(vacina.id))
  );

  const alternar = async (unitId: string | null, vacina: VaccineType) => {
    const atuais = unitId === null ? [...daEmpresa] : daUnidade(unitId);
    const id = String(vacina.id);
    const vaccineTypeIds = atuais.includes(id) ? atuais.filter((atual) => atual !== id) : [...atuais, id];

    setGravando(true);
    setErro(null);
    try {
      setGravadas(await vaccineRequirementService.replace(companyId, { unitId, vaccineTypeIds }));
    } catch (err: unknown) {
      setErro(getApiErrorMessage(err, 'Erro ao gravar as vacinas exigidas'));
    } finally {
      setGravando(false);
    }
  };

  const carregando = !catalogo.resolvida || !requisitos.resolvida || !unidades.resolvida;
  const desabilitada = alterar.disabled || gravando;

  const conteudo = (): React.ReactNode => {
    if (carregando) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    const falha = requisitos.bloqueio ?? requisitos.erro ?? catalogo.bloqueio ?? catalogo.erro;
    if (falha) {
      return (
        <Typography variant="body2" color="text.secondary">
          {falha}
        </Typography>
      );
    }

    return (
      <>
        {ativas.length === 0 && (
          <Box sx={{ mb: 3 }}>
            <PrerequisiteBlock
              title="Sem vacinas para escolher"
              reason="Ainda não há vacina ativa no catálogo, e é dele que a empresa escolhe as que exige."
              resolve={{
                label: 'Abrir catálogo de vacinas',
                permission: 'vaccine-type.create',
                mode: 'navigate',
                route: '/vaccine-types',
              }}
              deniedHint="Peça ao administrador da plataforma para cadastrar as vacinas no catálogo."
            />
          </Box>
        )}

        {alterar.disabled && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {alterar.tooltip}
          </Typography>
        )}

        {erro && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
            {erro}
          </Alert>
        )}

        {vacinas.length > 0 && (
          <>
            <ListaDeVacinas
              titulo="Exigidas da empresa toda"
              descricao="Valem para todos os funcionários da empresa."
              opcoes={vacinas.map((vacina) => ({
                vacina,
                marcada: daEmpresa.has(String(vacina.id)),
                travada: false,
                legenda: legendaDaVacina(vacina),
              }))}
              desabilitada={desabilitada}
              onAlternar={(vacina) => alternar(null, vacina)}
            />

            {unidades.itens.map((unidade) => {
              const unitId = String(unidade.id);
              const daquiDaUnidade = new Set(daUnidade(unitId));
              return (
                <ListaDeVacinas
                  key={unitId}
                  titulo={`${unidade.name} · soma à empresa`}
                  descricao="Só para quem trabalha nesta unidade, além das exigidas da empresa toda."
                  opcoes={vacinas.map((vacina) => {
                    const id = String(vacina.id);
                    const travada = daEmpresa.has(id);
                    return {
                      vacina,
                      marcada: travada || daquiDaUnidade.has(id),
                      travada,
                      legenda: travada ? 'da empresa' : legendaDaVacina(vacina),
                    };
                  })}
                  desabilitada={desabilitada}
                  onAlternar={(vacina) => alternar(unitId, vacina)}
                />
              );
            })}

            {unidades.bloqueio && (
              <Typography variant="caption" color="text.secondary">
                {unidades.bloqueio}
              </Typography>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <ProfileSection title="Vacinas exigidas" icon={<VaccinesIcon />}>
      {conteudo()}
    </ProfileSection>
  );
};

export default CompanyVaccinesTab;
